import {
    type ApiError,
    type TapeColumnMapping,
    type TapeRole,
    type TapeUpload,
} from '@lightdash/common';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { lightdashApi } from '../../api';

const CHUNK_BYTES = 32 * 1024 * 1024; // each request stays well under proxy timeouts

const base = (projectUuid: string) => `/projects/${projectUuid}/tapes`;

export const useTapes = (projectUuid: string, poll: boolean) =>
    useQuery<TapeUpload[], ApiError>({
        queryKey: ['tapes', projectUuid],
        queryFn: () =>
            lightdashApi<TapeUpload[]>({
                url: `${base(projectUuid)}/`,
                method: 'GET',
                body: undefined,
            }),
        refetchInterval: poll ? 3000 : false,
    });

/** start -> chunked PUTs (resumable by offset) -> complete (returns proposals). */
export const useUploadTape = (
    projectUuid: string,
    onProgress: (fraction: number) => void,
) =>
    useMutation<
        TapeUpload,
        ApiError,
        { file: File; dealId: string; role: TapeRole }
    >({
        mutationFn: async ({ file, dealId, role }) => {
            const tape = await lightdashApi<TapeUpload>({
                url: `${base(projectUuid)}/`,
                method: 'POST',
                body: JSON.stringify({
                    dealId,
                    role,
                    filename: file.name,
                    sizeBytes: file.size,
                }),
            });
            for (let offset = 0; offset < file.size; offset += CHUNK_BYTES) {
                await lightdashApi<{ received: number }>({
                    url: `${base(projectUuid)}/${tape.tapeUuid}/content?offset=${offset}`,
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/octet-stream' },
                    body: file.slice(offset, offset + CHUNK_BYTES),
                });
                onProgress(Math.min(1, (offset + CHUNK_BYTES) / file.size));
            }
            return lightdashApi<TapeUpload>({
                url: `${base(projectUuid)}/${tape.tapeUuid}/complete`,
                method: 'POST',
                body: undefined,
            });
        },
    });

export const useCommitTape = (projectUuid: string) => {
    const queryClient = useQueryClient();
    return useMutation<
        TapeUpload,
        ApiError,
        { tapeUuid: string; mapping: Record<string, TapeColumnMapping> }
    >({
        mutationFn: ({ tapeUuid, mapping }) =>
            lightdashApi<TapeUpload>({
                url: `${base(projectUuid)}/${tapeUuid}/commit`,
                method: 'POST',
                body: JSON.stringify({ mapping }),
            }),
        onSuccess: () =>
            queryClient.invalidateQueries({ queryKey: ['tapes', projectUuid] }),
    });
};
